import React, {forwardRef, useEffect} from 'react';

import {
    ButtonBase,
    MenuItem,
    Slide, Stack} from '@mui/material';

import {yupResolver} from '@hookform/resolvers/yup';

import lineups from 'assets/lineups/home';
import {sizeOptions} from 'canvas/Drawer/player-utils';
import {
    Dialog, HiddenFileInput, SaveButton, Text} from 'components';
import {useTranslation} from 'hooks';
import {useApiQuery} from 'module/client/useClientApi';
import {useLayoutStore, useSnackbarStore} from 'store';

import {schema, useApiUpdate, useApiUpload} from './useTeamApi';

import {Form, FormField, SelectField,TeamColorPickerField,useForm} from 'form';

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

import Icons from 'assets/icons';
import {imageUrl} from 'lib/media';
import UserSelectOnDemand from 'module/client/UserSelect';
import {useLicenseManager} from 'module/user/useLicenseManager';

const styles = {
    teamLogoHolder: {
        width: '100%',
        position: 'relative',
        paddingTop: '30px',
        paddingBottom: '30px',
        '& img': {
            display: 'block',
            maxWidth: '100%',
            height: 'auto',
            maxHeight: '750px'
        },
    },
    absolutIconBox: {
        position: 'absolute',
        bottom: '0',
        backgroundColor: 'red.highlight',
        borderRadius: '50%',
        height: 60,
        width: 60,
    },
    absolutIconBoxWithoutImage: {
        backgroundColor: 'red.highlight',
        borderRadius: '50%',
        height: 60,
        width: 60,
    },
    uploadButton: {
        backgroundColor: 'red.highlight',
        padding: 1,
        '> svg': {
            fill: theme => theme.palette.white.main,
            width: 18,
            height: 18
        }
    },
    uploadButtonWithoutImage: {
        backgroundColor: 'red.highlight',
        '> svg': {
            fill: theme => theme.palette.white.main,
            width: 30,
            height: 30
        }
    },
    fieldContainer: {
        mb: 2
    },
};

const Edit = ({team, onClose, onSave, client}) => {
    const translate = useTranslation();

    const {hasAccess, hasFullAccess} = useLicenseManager();
    const {data: clients} = useApiQuery();

    const showInfo = useSnackbarStore((state) => state.show);

    const setShowLoading = useLayoutStore((state) => state.setShowLoading);

    const {update, loading: loading1, data, errors} = useApiUpdate(team);
    const {upload, loading: loading2} = useApiUpload();

    const rhf = useForm(data, errors, true, {resolver: yupResolver(schema)});

    const doSave = () => {
        rhf.methods.handleSubmit(async formData => {
            formData.id = team.id;

            try {
                const res = await update(formData);
                showInfo('team.action.save_success');
                onSave && onSave(res);
            } catch (e) {
                showInfo(e.message, {severity: 'warning'});
            }
        })();
    };

    const doUpload = async (e) => {
        if (e.target.files.length === 1) {
            const res = await upload(team.id, e.target.files[0]);
            e.target.value = null;
            onSave && onSave(res);
        }
    };

    useEffect(() => {
        setShowLoading(loading1 || loading2);
        return () => {
            setShowLoading(false);
        };
    }, [loading1, loading2, setShowLoading]);

    // Get default colors from client if there is a color defined in the client
    const getDefaultColors = () => {
        // First try to get colors from the passed client prop
        if (client?.colors?.value1 && client?.colors?.value2) {
            return {
                primary: client.colors.value1,
                secondary: client.colors.value2
            };
        }
        
        // If no client prop, try to get client by team ID
        const teamClient = !client ? clients?.find(client => client.id === team.client_id) : client;
        if (teamClient?.colors?.value1 && teamClient?.colors?.value2) {
            return {
                primary: teamClient.colors.value1,
                secondary: teamClient.colors.value2
            };
        }
        
        // Fallback to default colors
        return {
            primary: '#ffffff',
            secondary: '#000000'
        };
    };

    const defaultColors = getDefaultColors();

    const playerColors = {
        color: rhf.watch('prefs.player.color1') || team.prefs.player.color1 || defaultColors.primary,
        bgcolor: rhf.watch('prefs.player.color2') || team.prefs.player.color2 || defaultColors.secondary
    };
    return (
        <Dialog
            maxWidth={false}
            pt={0}
            open={true}
            TransitionComponent={Transition}
            title="team.action.edit"
            handleClose={onClose}
            dividers={true}
        >
            <Stack sx={styles.teamLogoHolder} justifyContent="center" alignItems="center">
                {team.image && <img src={imageUrl(team.image, {w: 300})} alt={team.name} />}
                <Stack sx={team.image ? styles.absolutIconBox : styles.absolutIconBoxWithoutImage} alignItems="center" justifyContent="center">
                    <ButtonBase component="label" sx={team.image ? styles.uploadButton : styles.uploadButtonWithoutImage}>
                        <Icons.Camera />
                        <HiddenFileInput name="file" onChange={doUpload}/>
                    </ButtonBase>
                </Stack>
            </Stack>
              
            <Form methods={rhf.methods}>
                <Stack direction="column" justifyContent="flex-end" p={2}>
                    <Stack sx={styles.fieldContainer}>
                        <SelectField label="board.lineup.display" name="type">
                            <MenuItem
                                key={'home'}
                                value={'home'}>{translate('board.lineup.value.team')}
                            </MenuItem>
                            <MenuItem
                                key={'guest'}
                                value={'guest'}>{translate('board.lineup.value.opponent')}

                            </MenuItem>

                        </SelectField>
                    </Stack>

                    <Stack sx={styles.fieldContainer}>
                        <FormField
                            name="name"
                            label="team.name"
                        />
                    </Stack>

                    <Stack sx={styles.fieldContainer}>
                        <FormField
                            name="num_players"
                            label="team.prefs.player.count"
                            type="number"
                        />
                    </Stack>

                    <Stack sx={styles.fieldContainer}>
                        <SelectField label="board.lineup.preferred" name="lineup">
                            {lineups.map(lineup => <MenuItem
                                key={lineup.name}
                                value={lineup.name}>{lineup.name}</MenuItem>)}
                        </SelectField>
                    </Stack>
                    {hasFullAccess('colors') && (
                        <Stack sx={styles.fieldContainer}>
                            <TeamColorPickerField
                                primaryColor={playerColors.color}
                                secondaryColor={playerColors.bgcolor}
                                onPrimaryColorChange={(color) => rhf.methods.setValue('prefs.player.color1', color)}
                                onSecondaryColorChange={(color) => rhf.methods.setValue('prefs.player.color2', color)}
                            />
                        </Stack>
                    )}

                    <Stack sx={styles.fieldContainer}>
                        <SelectField label="team.prefs.player.size" name="prefs.player.size">
                            {Object.keys(sizeOptions).map((key, index) => {
                                if (hasAccess('player-sizes', sizeOptions[key].label).access) {
                                    return (
                                        <MenuItem
                                            key={key}
                                            value={sizeOptions[index + 1].label}>{sizeOptions[index + 1].label}</MenuItem>
                                    );
                                }
                            }
                            )}
                        </SelectField>
                    </Stack>

                    <Stack sx={styles.fieldContainer}>
                        <SelectField label="team.prefs.player.type" name="prefs.player.type">
                            <MenuItem value="player"><Text>team.prefs.player.display.shirts</Text></MenuItem>
                            <MenuItem value="playerCircle"><Text>team.prefs.player.display.magnets</Text></MenuItem>
                            {hasAccess('player-image').access && (<MenuItem value="playerCircleImage"><Text>team.prefs.player.display.magnet_image</Text></MenuItem>)}
                        </SelectField>
                    </Stack>

                    {/* <Stack sx={styles.fieldContainer}>
                        <SelectField label="team.prefs.player.image" name="prefs.player.image">
                            <MenuItem value={true}><Text>global.yes</Text></MenuItem>
                            <MenuItem value={false}><Text>global.no</Text></MenuItem>
                        </SelectField>
                    </Stack> */}

                    <Stack sx={styles.fieldContainer}>
                        <UserSelectOnDemand name="managers" label="team.member.managers" multiple disableClearable clientId={team.client_id}/>
                    </Stack>

                    <SaveButton action={doSave} />

                </Stack>
            </Form>
        </Dialog>
    );
};

export default Edit;